import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Header } from './components/Header';
import { StatsCard } from './components/StatsCard';
import { TicketTable } from './components/TicketTable';
import { UserStatsPanel } from './components/UserStatsPanel';
import { PrintReport } from './components/PrintReport';
import { CoverPage } from './components/CoverPage';
import { SuperAdminPage } from './components/SuperAdminPage';
import { EventAdminPage } from './components/EventAdminPage';
import LearnMore from './components/LearnMore';
import { APP_VERSION } from './version';
import { useVersionCheck } from './hooks/useVersionCheck';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function to update favicon and page title based on event
const updatePageMeta = (event) => {
  const favicon = document.getElementById('dynamic-favicon') || document.querySelector('link[rel="icon"]');
  const description = document.getElementById('dynamic-description') || document.querySelector('meta[name="description"]');
  
  if (!event) {
    // Cover page - TicketASaurus branding
    document.title = 'TicketASaurus | Home Game Ticket Tracker';
    if (favicon) favicon.href = '/ticketasaurus-logo.png';
    if (description) description.content = 'TicketASaurus - Home Game Ticket Tracker';
    return;
  }
  
  // Event-specific branding
  const eventName = event.name || 'Event';
  const season = event.season || '2025-2026';
  const sport = (event.sport || 'sports').toLowerCase();
  
  // Set title
  document.title = `${eventName} | ${season} Season Tickets`;
  
  // Set favicon based on event
  if (favicon) {
    if (eventName.toLowerCase().includes('mavericks') || sport === 'basketball') {
      favicon.href = '/dallas-mavs-logo.png';
    } else if (eventName.toLowerCase().includes('stars') || sport === 'hockey') {
      favicon.href = '/dallas-stars-logo.svg';
    } else if (eventName.toLowerCase().includes('cowboys') || sport === 'football') {
      favicon.href = '/dallas-cowboys-logo.png';
    } else if (eventName.toLowerCase().includes('rangers') || sport === 'baseball') {
      favicon.href = '/texas-rangers-logo.png';
    } else {
      favicon.href = '/ticketasaurus-logo.png';
    }
  }
  
  // Set description
  if (description) {
    description.content = `${eventName} ${season} Season Ticket Tracker`;
  }
};

// Season configuration - can be changed for different teams/seasons
const SEASON_CONFIG = {
  teamName: 'Dallas Mavericks',
  sport: 'Basketball',
  season: '2025-2026',
  teamLogo: '🏀',
  primaryColor: '#0053BC',
  secondaryColor: '#00285E'
};

// Available seasons/events for the selector
const AVAILABLE_SEASONS = [
  {
    teamName: 'Dallas Mavericks',
    sport: 'Basketball',
    season: '2025-2026',
    primaryColor: '#0053BC',
    secondaryColor: '#00285E'
  }
];

const gamesData = {
  team: 'Dallas Mavericks',
  arena: 'American Airlines Center',
  season: '2025-26',
  homeGames: [
    { type: 'preseason', date: '2025-10-11', opponent: 'Charlotte Hornets', timeCT: '7:30 PM' },

    { type: 'regular', date: '2025-10-22', opponent: 'San Antonio Spurs', timeCT: '8:30 PM' },
    { type: 'regular', date: '2025-10-24', opponent: 'Washington Wizards', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-10-26', opponent: 'Toronto Raptors', timeCT: '6:30 PM' },
    { type: 'regular', date: '2025-10-27', opponent: 'Oklahoma City Thunder', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-10-29', opponent: 'Indiana Pacers', timeCT: '7:30 PM' },

    { type: 'regular', date: '2025-11-05', opponent: 'New Orleans Pelicans', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-11-10', opponent: 'Milwaukee Bucks', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-11-12', opponent: 'Phoenix Suns', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-11-14', opponent: 'Los Angeles Clippers', note: 'NBA Cup group', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-11-16', opponent: 'Portland Trail Blazers', timeCT: '6:30 PM' },
    { type: 'regular', date: '2025-11-19', opponent: 'New York Knicks', timeCT: '8:30 PM' },
    { type: 'regular', date: '2025-11-21', opponent: 'New Orleans Pelicans', note: 'NBA Cup group', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-11-22', opponent: 'Memphis Grizzlies', timeCT: '7:30 PM' },

    { type: 'regular', date: '2025-12-03', opponent: 'Miami Heat', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-12-06', opponent: 'Houston Rockets', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-12-18', opponent: 'Detroit Pistons', timeCT: '7:30 PM' },
    { type: 'regular', date: '2025-12-23', opponent: 'Denver Nuggets', timeCT: '7:00 PM' },

    { type: 'regular', date: '2026-01-01', opponent: 'Philadelphia 76ers', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-01-03', opponent: 'Houston Rockets', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-01-12', opponent: 'Brooklyn Nets', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-01-14', opponent: 'Denver Nuggets', timeCT: '8:30 PM' },
    { type: 'regular', date: '2026-01-15', opponent: 'Utah Jazz', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-01-17', opponent: 'Utah Jazz', timeCT: '4:00 PM' },
    { type: 'regular', date: '2026-01-22', opponent: 'Golden State Warriors', timeCT: '6:30 PM' },
    { type: 'regular', date: '2026-01-24', opponent: 'Los Angeles Lakers', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-01-28', opponent: 'Minnesota Timberwolves', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-01-29', opponent: 'Charlotte Hornets', timeCT: '7:30 PM' },

    { type: 'regular', date: '2026-02-03', opponent: 'Boston Celtics', timeCT: '7:00 PM' },
    { type: 'regular', date: '2026-02-05', opponent: 'San Antonio Spurs', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-02-26', opponent: 'Sacramento Kings', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-02-27', opponent: 'Memphis Grizzlies', timeCT: '7:30 PM' },

    { type: 'regular', date: '2026-03-01', opponent: 'Oklahoma City Thunder', timeCT: '7:00 PM' },
    { type: 'regular', date: '2026-03-13', opponent: 'Cleveland Cavaliers', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-03-18', opponent: 'Atlanta Hawks', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-03-21', opponent: 'Los Angeles Clippers', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-03-23', opponent: 'Golden State Warriors', timeCT: '8:30 PM' },
    { type: 'regular', date: '2026-03-30', opponent: 'Minnesota Timberwolves', timeCT: '7:30 PM' },

    { type: 'regular', date: '2026-04-03', opponent: 'Orlando Magic', timeCT: '7:30 PM' },
    { type: 'regular', date: '2026-04-05', opponent: 'Los Angeles Lakers', timeCT: '6:30 PM' },
    { type: 'regular', date: '2026-04-12', opponent: 'Chicago Bulls', timeCT: '7:30 PM' }
  ]
};

const Dashboard = ({ currentUser, onLogout, currentEvent, initialMobileView = null, initialManualOverride = false }) => {
  const location = useLocation();
  const [ticketData, setTicketData] = useState({});
  const [record, setRecord] = useState({ wins: 0, losses: 0 }); // Home record
  const [seasonRecord, setSeasonRecord] = useState({ wins: 0, losses: 0 }); // Overall season record
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [appSettings, setAppSettings] = useState(null);
  const [gamesFromDB, setGamesFromDB] = useState([]);
  const [ipAddress, setIpAddress] = useState('');
  const [partnerOptions, setPartnerOptions] = useState([{ id: 'F', name: 'Finley' }, { id: 'B', name: 'Bill' }]); // Dynamic partner options with fallback
  
  // Get event ID from current event or default
  const eventId = currentEvent?.id || (localStorage.getItem('currentEvent') 
    ? JSON.parse(localStorage.getItem('currentEvent') || '{}')?.id 
    : 'mavericks-2025');
  
  // Update page meta (favicon, title) when event changes
  useEffect(() => {
    updatePageMeta(currentEvent);
  }, [currentEvent]);
  
  // Detect screen size: mobile (<768px), tablet (768px-1366px), desktop (>1366px)
  // Use initialMobileView if provided from CoverPage selection
  const [isMobileView, setIsMobileView] = useState(() => {
    if (initialMobileView !== null) return initialMobileView;
    const width = window.innerWidth;
    // Default: mobile view for mobile AND tablets (up to 1366px), desktop view only for large screens
    return width <= 1366;
  });
  const [manualOverride, setManualOverride] = useState(initialManualOverride);
  const [isTablet, setIsTablet] = useState(() => {
    const width = window.innerWidth;
    // Tablet range: 768px to 1366px (includes iPad Pro, landscape tablets)
    return width >= 768 && width <= 1366;
  });
  
  const [stats, setStats] = useState({
    totalGames: 0,
    totalTickets: 0,
    totalParking: 0,
    usedTickets: 0,
    usedParking: 0,
    partnerStats: []
  });

  const [showPrintReport, setShowPrintReport] = useState(false);

  // Fetch IP address
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setIpAddress(data.ip);
      } catch (err) {
        setIpAddress('Unknown');
      }
    };
    fetchIP();
  }, []);

  // Fetch partner options based on event's assigned users
  useEffect(() => {
    const fetchPartnerOptions = async () => {
      try {
        // Get all users to map emails to partner_ids
        const usersRes = await axios.get(`${API}/users/`);
        const users = usersRes.data.users || [];
        
        // Get assigned users for current event
        let assignedEmails = currentEvent?.assigned_users || [];
        
        // If currentEvent doesn't have assigned_users, fetch from API
        if (assignedEmails.length === 0 && eventId) {
          try {
            const eventsRes = await axios.get(`${API}/events/`);
            const events = eventsRes.data.events || [];
            const thisEvent = events.find(e => e.id === eventId);
            if (thisEvent && thisEvent.assigned_users) {
              assignedEmails = thisEvent.assigned_users;
            }
          } catch (e) {
            console.error('Error fetching event details:', e);
          }
        }
        
        // Map assigned users to their partner_ids
        const partners = users
          .filter(u => assignedEmails.includes(u.email) && u.partner_id)
          .map(u => ({ id: u.partner_id, name: u.name }));
        
        // Fallback to default F, B if no partners assigned (legacy support)
        if (partners.length === 0) {
          setPartnerOptions([{ id: 'F', name: 'Finley' }, { id: 'B', name: 'Bill' }]);
        } else {
          setPartnerOptions(partners);
        }
      } catch (error) {
        console.error('Error fetching partner options:', error);
        // Fallback to defaults
        setPartnerOptions([{ id: 'F', name: 'Finley' }, { id: 'B', name: 'Bill' }]);
      }
    };
    fetchPartnerOptions();
  }, [currentEvent, eventId]);

  // Load app settings and games from database for the current event
  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, gamesRes, globalSettingsRes] = await Promise.all([
          axios.get(`${API}/settings/?event_id=${eventId}`),
          axios.get(`${API}/games/schedule?event_id=${eventId}${(currentEvent?.sport === 'family' || currentEvent?.sport === 'concerts') && currentUser?.email ? `&user_email=${encodeURIComponent(currentUser.email)}` : ''}`),
          axios.get(`${API}/superadmin/global-settings`)
        ]);
        
        if (settingsRes.data.success) {
          // Merge global footer with event-specific settings
          const eventSettings = settingsRes.data.settings;
          if (globalSettingsRes.data?.success) {
            eventSettings.footer_text = globalSettingsRes.data.settings.footer_text || '';
          }
          setAppSettings(eventSettings);
        }
        
        if (gamesRes.data.success) {
          // If games exist, use them; otherwise start with empty for new events
          setGamesFromDB(gamesRes.data.games || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, [location, eventId, currentEvent]);

  // Listen for window resize to auto-switch views (only if not manually overridden)
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Tablet range: 768px to 1366px
      setIsTablet(width >= 768 && width <= 1366);
      
      if (!manualOverride) {
        // Default: mobile view for mobile and tablets (up to 1366px), desktop view only for large screens
        setIsMobileView(width <= 1366);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [manualOverride]);

  const calculateStats = (data) => {
    let usedTickets = 0;
    let usedParking = 0;
    
    // Create dynamic partner stats based on partnerOptions
    const partnerCounts = {};
    partnerOptions.forEach(p => {
      partnerCounts[p.id] = {
        id: p.id,
        name: p.name,
        tickets: 0,
        parking: 0,
        regularTickets: 0,
        playoffTickets: 0,
        used: 0,
        balance: 0
      };
    });
    
    Object.entries(data).forEach(([gameIdx, game]) => {
      // Find the actual game to determine if it's playoff
      const actualGame = gamesFromDB.find(g => g.game_index === parseInt(gameIdx));
      const isPlayoffGame = actualGame && (actualGame.season_type === 'playoff' || actualGame.type === 'playoff');
      
      game.tickets.forEach(t => {
        if (t && partnerCounts[t]) {
          partnerCounts[t].tickets++;
          if (isPlayoffGame) partnerCounts[t].playoffTickets++;
          else partnerCounts[t].regularTickets++;
        }
      });
      game.parking.forEach(p => {
        if (p && partnerCounts[p]) {
          partnerCounts[p].parking++;
        }
      });
      usedTickets += game.tickets.filter(t => t !== '').length;
      usedParking += game.parking.filter(p => p !== '').length;
    });
    
    // Calculate cost per ticket dynamically
    const regularGames = gamesFromDB.filter(g => g.season_type !== 'playoff' && g.type !== 'playoff').length;
    const playoffGames = gamesFromDB.filter(g => g.season_type === 'playoff' || g.type === 'playoff').length;
    const totalSeats = appSettings?.total_seats || 6;
    
    // Get total regular season contributions from all partners
    const totalRegularContributions = appSettings?.partners?.reduce((sum, p) => sum + (p.amount_paid || 0), 0) || 0;
    
    // Calculate cost per regular season ticket
    const REGULAR_TICKET_VALUE = regularGames > 0 && totalSeats > 0 
      ? totalRegularContributions / (regularGames * totalSeats)
      : 0;
    
    // Get playoff contributions and calculate playoff ticket cost
    const totalPlayoffContributions = appSettings?.playoff_partners?.reduce((sum, p) => sum + (p?.amount_paid || 0), 0) || 0;
    const PLAYOFF_TICKET_VALUE = totalPlayoffContributions > 0 && playoffGames > 0 && totalSeats > 0
      ? totalPlayoffContributions / (playoffGames * totalSeats)
      : 0;
    
    // Calculate financial data for each partner
    Object.keys(partnerCounts).forEach(partnerId => {
      const partnerData = partnerCounts[partnerId];
      
      // Get regular contribution
      const regularPartner = appSettings?.partners?.find(p => p.id === partnerId);
      const regularContribution = regularPartner?.amount_paid || 0;
      
      // Get playoff contribution
      const playoffPartner = appSettings?.playoff_partners?.find(p => p?.id === partnerId);
      const playoffContribution = playoffPartner?.amount_paid || 0;
      
      // Calculate usage
      const regularUsed = partnerData.regularTickets * REGULAR_TICKET_VALUE;
      const playoffUsed = partnerData.playoffTickets * PLAYOFF_TICKET_VALUE;
      
      partnerData.used = regularUsed + playoffUsed;
      partnerData.balance = (regularContribution + playoffContribution) - partnerData.used;
    });
    
    // Convert partnerCounts to array for rendering
    const partnerStatsArray = Object.values(partnerCounts);
    
    return {
      totalGames: gamesFromDB.length,
      totalTickets: gamesFromDB.length * (appSettings?.total_seats || 6),
      totalParking: gamesFromDB.length * (appSettings?.total_parking ?? 2),
      usedTickets,
      usedParking,
      partnerStats: partnerStatsArray
    };
  };

  const fetchWinLossRecord = async () => {
    try {
      const response = await axios.get(`${API}/tickets/record?event_id=${eventId}`);
      setRecord(response.data);
      // Keep ties in state so Header can render W-L-T when any ties exist
      if (response.data?.ties !== undefined) {
        setRecord(prev => ({ ...prev, ties: response.data.ties }));
      }
    } catch (error) {
      console.error('Error fetching win/loss record:', error);
    }
  };

  const fetchSeasonRecord = async () => {
    try {
      const response = await axios.get(`${API}/nba/results?event_id=${eventId}`);
      // Set season (overall) record
      setSeasonRecord({ wins: response.data.wins, losses: response.data.losses });
      // Set home record - use nullish coalescing to handle 0 properly
      setRecord({ 
        wins: response.data.home_wins ?? response.data.wins ?? 0, 
        losses: response.data.home_losses ?? response.data.losses ?? 0 
      });
    } catch (error) {
      console.error('Error fetching season record:', error);
    }
  };

  // Reset data and fetch records when location changes
  useEffect(() => {
    // Fetch win/loss records on location change
    fetchWinLossRecord();
    fetchSeasonRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, eventId]);
  
  // Reset ticket data when location changes (navigating back from admin)
  useEffect(() => {
    setTicketData({});
  }, [location]);

  // Use useMemo for stats calculation instead of useEffect to avoid cascading renders
  const calculatedStats = React.useMemo(() => {
    if (Object.keys(ticketData).length > 0 && appSettings && gamesFromDB.length > 0) {
      return calculateStats(ticketData);
    }
    return stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData, appSettings, gamesFromDB, partnerOptions]);
  
  // Update stats state when calculated stats change
  useEffect(() => {
    if (calculatedStats !== stats) {
      setStats(calculatedStats);
    }
  }, [calculatedStats, stats]);

  const handleDataChange = (newData) => {
    setTicketData(newData);
    setStats(calculateStats(newData));
    // Refetch win/loss record when data changes
    fetchWinLossRecord();
  };

  const handleDataRefresh = () => {
    // Force refresh all data when user logs in
    fetchWinLossRecord();
    fetchSeasonRecord();
    
    // Determine which score API to call based on sport type
    const sport = currentEvent?.sport?.toLowerCase();
    const eventId = currentEvent?.id || 'mavericks-2025';
    
    // Skip score fetching for non-sports events
    if (sport === 'family' || sport === 'concerts') {
      console.log('Skipping score fetch for non-sports event');
      setRefreshTrigger(prev => prev + 1);
      return;
    }
    
    let scoreEndpoint = `${API}/auth/update-scores?event_id=${eventId}`; // Default: NBA
    if (sport === 'hockey') {
      scoreEndpoint = `${API}/auth/update-nhl-scores?event_id=${eventId}`;
    } else if (sport === 'football') {
      scoreEndpoint = `${API}/auth/update-nfl-scores?event_id=${eventId}`;
    }
    
    // Fetch latest scores from appropriate API
    axios.post(scoreEndpoint)
      .then(response => {
        console.log('Scores updated:', response.data);
        // Trigger TicketTable to reload data after scores are updated
        setRefreshTrigger(prev => prev + 1);
      })
      .catch(error => {
        console.error('Error updating scores:', error);
        // Still trigger refresh even if API fails
        setRefreshTrigger(prev => prev + 1);
      });
  };
  
  // Auto-check scores on login (triggered by flag from AppContent)
  useEffect(() => {
    const shouldCheckScores = sessionStorage.getItem('triggerScoreCheck');
    if (shouldCheckScores === 'true') {
      // Clear the flag
      sessionStorage.removeItem('triggerScoreCheck');
      // Wait a bit for component to fully mount, then refresh scores
      const timer = setTimeout(() => {
        console.log('Auto-checking scores on login...');
        handleDataRefresh();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []); // Only run on mount

  // Get dynamic background style based on event
  const getBackgroundStyle = () => {
    // Use epic stadium background as base
    return {
      background: 'radial-gradient(ellipse at top, #1a0a2e 0%, #0a0a1a 50%, #0d0d1f 100%)'
    };
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={getBackgroundStyle()}
    >
      {/* Epic Background Elements - Optimized for performance */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {/* Static spotlight beams */}
        <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/40 via-cyan-400/10 to-transparent transform -skew-x-12 blur-sm"></div>
        <div className="absolute top-0 left-1/3 w-2 h-full bg-gradient-to-b from-purple-500/30 via-purple-500/5 to-transparent transform skew-x-6 blur-md"></div>
        <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-cyan-400/35 via-cyan-400/10 to-transparent transform skew-x-12 blur-sm"></div>
        <div className="absolute top-0 right-1/3 w-2 h-full bg-gradient-to-b from-blue-500/25 via-blue-500/5 to-transparent transform -skew-x-6 blur-md"></div>
        
        {/* Static glowing orbs - no animation */}
        <div className="absolute top-[5%] left-[10%] w-32 h-32 bg-cyan-400/30 rounded-full blur-[60px]"></div>
        <div className="absolute top-[5%] right-[10%] w-32 h-32 bg-purple-500/30 rounded-full blur-[60px]"></div>
        <div className="absolute top-[3%] left-[30%] w-24 h-24 bg-blue-400/25 rounded-full blur-[50px]"></div>
        <div className="absolute top-[3%] right-[30%] w-24 h-24 bg-teal-400/25 rounded-full blur-[50px]"></div>
        
        {/* Ambient color zones */}
        <div className="absolute top-1/4 left-0 w-[50%] h-[60%] bg-gradient-to-r from-purple-600/15 via-purple-600/5 to-transparent blur-[100px]"></div>
        <div className="absolute top-1/4 right-0 w-[50%] h-[60%] bg-gradient-to-l from-cyan-500/15 via-cyan-500/5 to-transparent blur-[100px]"></div>
        
        {/* Hexagon pattern */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2322d3ee' fill-opacity='0.4'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
        
        {/* Bottom glow and vignettes */}
        <div className="absolute bottom-0 left-0 right-0 h-48" style={{ background: 'linear-gradient(to top, rgba(34, 211, 238, 0.15) 0%, rgba(139, 92, 246, 0.08) 30%, transparent 100%)' }}></div>
        <div className="absolute top-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)' }}></div>
        <div className="absolute top-0 bottom-0 left-0 w-24" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.2) 0%, transparent 100%)' }}></div>
        <div className="absolute top-0 bottom-0 right-0 w-24" style={{ background: 'linear-gradient(to left, rgba(0,0,0,0.2) 0%, transparent 100%)' }}></div>
        
        {/* Static bokeh dots */}
        <div className="absolute top-[20%] left-[15%] w-3 h-3 bg-cyan-400/40 rounded-full blur-[2px]"></div>
        <div className="absolute top-[35%] right-[20%] w-2 h-2 bg-purple-400/30 rounded-full blur-[2px]"></div>
        <div className="absolute top-[60%] left-[8%] w-2 h-2 bg-blue-400/30 rounded-full blur-[1px]"></div>
        <div className="absolute top-[70%] right-[12%] w-2 h-2 bg-teal-400/20 rounded-full blur-[2px]"></div>
      </div>
      <div className="relative z-10">
      <Header 
        wins={record.wins} 
        losses={record.losses}
        ties={record.ties || 0}
        seasonWins={seasonRecord.wins}
        seasonLosses={seasonRecord.losses}
        totalGames={stats.totalGames}
        usedTickets={stats.usedTickets}
        totalTickets={stats.totalTickets}
        usedParking={stats.usedParking}
        totalParking={stats.totalParking}
        partnerStats={stats.partnerStats || []}
        onDataRefresh={handleDataRefresh}
        isMobileView={isMobileView}
        isTablet={isTablet}
        onToggleMobileView={() => {
          setManualOverride(true);
          setIsMobileView(!isMobileView);
        }}
        onPrintReport={() => setShowPrintReport(true)}
        games={gamesFromDB}
        ticketData={ticketData}
        appSettings={appSettings}
        currentUser={currentUser}
        currentEvent={currentEvent}
        ipAddress={ipAddress}
        onLogout={onLogout}
      />
      
      <main className={`${isMobileView ? 'max-w-md mx-auto px-3 py-2' : 'container mx-auto px-4 py-4'} space-y-3`}>

        {/* Ticket Table */}
        <TicketTable 
          games={gamesFromDB} 
          onDataChange={handleDataChange} 
          refreshTrigger={refreshTrigger}
          isMobileView={isMobileView}
          eventId={eventId}
          appSettings={appSettings}
          partnerOptions={partnerOptions}
          currentUser={currentUser}
          currentEvent={currentEvent}
        />

        {/* Footer Info */}
        <div className="text-center py-8 space-y-2">
          {appSettings?.footer_text ? (
            <p className="text-sm text-muted-foreground">
              {appSettings.footer_text} <span className="opacity-60">• {APP_VERSION}</span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {currentEvent?.name || gamesData.team} • {currentEvent?.season || gamesData.season} Season <span className="opacity-60">• {APP_VERSION}</span>
            </p>
          )}
        </div>
      </main>

      {/* Print Report Modal */}
      {showPrintReport && (
        <PrintReport
          games={gamesFromDB}
          ticketData={ticketData}
          stats={stats}
          appSettings={appSettings}
          currentEvent={currentEvent}
          partnerOptions={partnerOptions}
          currentUser={currentUser}
          onClose={() => setShowPrintReport(false)}
        />
      )}
    </div>
    </div>
  );
};

// Wrapper component that shows CoverPage when not authenticated
const AppContent = () => {
  useVersionCheck();
  const { isAuthenticated, login, logout, isEmulating, getEmulatedUser } = useAuth();
  const [showCover, setShowCover] = useState(() => {
    // Check if already authenticated or emulating on mount
    const isAuth = localStorage.getItem('isAuthenticated') === 'true';
    const isEmul = localStorage.getItem('isEmulating') === 'true';
    const isSwitching = sessionStorage.getItem('switchingEvents') === 'true';
    
    // If switching events or starting emulation without a current event, show cover page
    if (isSwitching || (isEmul && !localStorage.getItem('currentEvent'))) {
      return true;
    }
    
    return !isAuth && !isEmul;
  });
  const [currentUser, setCurrentUser] = useState(() => {
    // Check for emulation mode first
    const isEmul = localStorage.getItem('isEmulating') === 'true';
    if (isEmul) {
      return {
        email: localStorage.getItem('emulatedUserEmail'),
        name: localStorage.getItem('emulatedUserName'),
        partner_id: localStorage.getItem('emulatedUserPartnerId'),
        is_event_manager: localStorage.getItem('emulatedUserIsEventManager') === 'true'
      };
    }
    // Restore user from localStorage on mount
    const saved = localStorage.getItem('currentUser');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { return null; }
    }
    return null;
  });
  const [currentEvent, setCurrentEvent] = useState(() => {
    // Restore event from localStorage on mount
    const saved = localStorage.getItem('currentEvent');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { return null; }
    }
    return null;
  });
  
  // View toggle state (for tablet users) - moved here so handleCoverLogin can access it
  // Default: mobile view for mobile AND tablets (up to 1366px), desktop view only for large screens
  const [isMobileViewApp, setIsMobileViewApp] = useState(() => {
    const width = window.innerWidth;
    return width <= 1366; // Mobile view for phones and tablets
  });
  const [manualOverrideApp, setManualOverrideApp] = useState(false);

  const handleCoverLogin = async (pin, isGuest = false, user = null, event = null, viewOverride = null) => {
    // Apply view override if set (for tablet users)
    if (viewOverride) {
      setManualOverrideApp(true);
      setIsMobileViewApp(viewOverride === 'mobile');
    }
    
    if (user) {
      // User logged in with email/password
      setCurrentUser(user);
      setCurrentEvent(event);
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', 'true');
      // Set flag to trigger score check in Dashboard
      sessionStorage.setItem('triggerScoreCheck', 'true');
      if (event) {
        localStorage.setItem('currentEvent', JSON.stringify(event));
      }
      setShowCover(false);
      // Also authenticate with PIN system for compatibility
      if (user.partner_id) {
        await login('2025'); // Default PIN for backward compatibility
      }
      return true;
    }
    
    if (isGuest) {
      // Guest mode - just show dashboard without authenticating
      setShowCover(false);
      // Set flag to trigger score check in Dashboard
      sessionStorage.setItem('triggerScoreCheck', 'true');
      return true;
    }
    
    const success = await login(pin);
    if (success) {
      setShowCover(false);
      // Set flag to trigger score check in Dashboard
      sessionStorage.setItem('triggerScoreCheck', 'true');
    }
    return success;
  };

  // Handle logout - reset to cover page
  const handleLogout = () => {
    // Check if we're in emulation mode - preserve emulation state
    const isEmulatingMode = localStorage.getItem('isEmulating') === 'true';
    const isSwitching = sessionStorage.getItem('switchingEvents') === 'true';
    
    if (isEmulatingMode && isSwitching) {
      // For emulated users switching events, preserve emulation state
      // Don't call logout() which clears emulation data
      setCurrentUser({
        email: localStorage.getItem('emulatedUserEmail'),
        name: localStorage.getItem('emulatedUserName'),
        partner_id: localStorage.getItem('emulatedUserPartnerId'),
        is_event_manager: localStorage.getItem('emulatedUserIsEventManager') === 'true'
      });
      setCurrentEvent(null);
      localStorage.removeItem('currentEvent');
      // Keep isAuthenticated true
      setShowCover(true);
    } else {
      // Normal logout - clear everything
      logout();
      setCurrentUser(null);
      setCurrentEvent(null);
      localStorage.removeItem('currentUser');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentEvent');
      setShowCover(true);
    }
  };

  return (
    <BrowserRouter>
      <PageviewTracker />
      <Routes>
        {/* SuperAdmin route - always accessible, has its own auth */}
        <Route path="/superadmin" element={<SuperAdminPage />} />
        <Route path="/event-admin" element={<EventAdminPage />} />
        <Route path="/learn-more" element={<LearnMore />} />
        
        {/* Dashboard route - accessible if authenticated */}
        <Route path="/dashboard" element={
          (localStorage.getItem('isAuthenticated') === 'true' || !showCover) ? (
            <Dashboard key={currentEvent?.id || 'default'} currentUser={currentUser} onLogout={handleLogout} currentEvent={currentEvent} initialMobileView={isMobileViewApp} initialManualOverride={manualOverrideApp} />
          ) : (
            <CoverPage 
              onLogin={handleCoverLogin}
              seasonConfig={SEASON_CONFIG}
              availableSeasons={AVAILABLE_SEASONS}
            />
          )
        } />
        
        {/* Main route - show cover page or dashboard */}
        <Route path="/" element={
          showCover ? (
            <CoverPage 
              onLogin={handleCoverLogin}
              seasonConfig={SEASON_CONFIG}
              availableSeasons={AVAILABLE_SEASONS}
            />
          ) : (
            <Dashboard key={currentEvent?.id || 'default'} currentUser={currentUser} onLogout={handleLogout} currentEvent={currentEvent} initialMobileView={isMobileViewApp} initialManualOverride={manualOverrideApp} />
          )
        } />
        
        {/* Catch all - redirect to main */}
        <Route path="*" element={
          showCover ? (
            <CoverPage 
              onLogin={handleCoverLogin}
              seasonConfig={SEASON_CONFIG}
              availableSeasons={AVAILABLE_SEASONS}
            />
          ) : (
            <Dashboard key={currentEvent?.id || 'default'} currentUser={currentUser} onLogout={handleLogout} currentEvent={currentEvent} initialMobileView={isMobileViewApp} initialManualOverride={manualOverrideApp} />
          )
        } />
      </Routes>
    </BrowserRouter>
  );
};

// Analytics pageview tracker — fires on every route change
const PageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    axios.post(`${API}/analytics/pageview`, { page: location.pathname }).catch(() => {});
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <AppContent />
      </div>
    </AuthProvider>
  );
}

export default App;
